import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5";

export const TrainingImport = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  const [exam, setExam] = useState("COC Certification Exam");
  const [emails, setEmails] = useState("");
  const [rows, setRows] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const parsed = rows.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const m = l.match(/^(.*?)[,:\t ]+(\d+(?:\.\d+)?)\s*%?$/);
      return m ? { name: m[1].trim(), score: parseFloat(m[2]) } : null;
    });
    const valid = parsed.filter(Boolean);
    const skipped = parsed.length - valid.length;
    if (!valid.length) {
      toast.error("No valid lines", { description: "One per line, e.g. Jordan Pike, 93" });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/training/results", {
        exam,
        manager_emails: emails.split(",").map((e) => e.trim()).filter(Boolean),
        results: valid,
      });
      toast.success(`Imported ${res.data.tested} results`, {
        description: res.data.needs_retake
          ? `${res.data.needs_retake} at or below the pass mark — retake alerts emailed to ${res.data.alerts_sent_to.length} manager(s)`
          : "Everyone passed — no retake alerts needed",
      });
      if (skipped) toast.info(`${skipped} line(s) skipped — no score found on the line`);
      setRows("");
      setOpen(false);
      onImported?.();
    } catch (err) {
      toast.error("Import failed", { description: typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Connection error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="technical-card p-6 mb-8" data-testid="training-import">
      <button onClick={() => setOpen(!open)} data-testid="training-import-toggle" className="w-full flex items-center justify-between text-left gap-4">
        <div className="flex items-center gap-3">
          <Upload size={15} className="text-sky-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Import COC exam results</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Paste real employee scores to replace the sample training chart — managers get retake alerts automatically.
            </p>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-muted-foreground shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="training-exam">Exam name</label>
              <input id="training-exam" data-testid="training-import-exam" className={inputCls} value={exam} onChange={(e) => setExam(e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor="training-emails">Manager alert emails (optional)</label>
              <input id="training-emails" data-testid="training-import-emails" className={inputCls} placeholder="manager@company.com, regional@company.com" value={emails} onChange={(e) => setEmails(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="training-rows">Scores — one per line as Name, score</label>
            <textarea
              id="training-rows"
              data-testid="training-import-rows"
              rows={6}
              className={`${inputCls} font-mono`}
              placeholder={"Jordan Pike, 93\nMarcus Bell, 85\nPriya Nair, 80"}
              value={rows}
              onChange={(e) => setRows(e.target.value)}
            />
          </div>
          <button
            data-testid="training-import-submit"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {saving ? "Importing…" : "Import & Send Retake Alerts"}
          </button>
        </div>
      )}
    </div>
  );
};
